import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FolderOpen,
  Printer,
  Info,
  Download,
  Settings2,
  GripVertical,
  Search,
  User as UserIcon,
  Settings,
  Bell,
  RefreshCw,
  Archive,
  AlertCircle,
  Edit,
  Trash2,
  Package,
  ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrackingLink } from "@/components/TrackingWidget";
import { useBranding } from "@/contexts/BrandingContext";
import { getStageLabel, getStatusLabel } from "@/utils/labelMapper";
import { getStatusColors, getStageColor, statusBadgeStyle } from "@/lib/statusColors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const fetchWithRetry = async (fetchFn, maxRetries = 3, delay = 1000) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;
      console.warn(`API call failed (attempt ${i + 1}/${maxRetries}):`, error.message);
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  throw lastError;
};

// ── Proof Thumbnails component ────────────────────────────────────────────────
// Shows the latest proofs for the order's current stage.
// Displays up to 3 thumbnails inline; a "+N" badge appears when there are more.
// Clicking a thumbnail opens a lightbox so admins can preview without navigating.
function ProofThumbnails({ order }) {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const stage = order.stage;
  // latest_proofs is injected by the backend (last 3 per stage).
  // Fall back to the full proof arrays if the field is present.
  const proofs =
    order.latest_proofs?.[stage] ||
    order[`${stage}_proofs`] ||
    [];

  const totalCount =
    order.proof_counts?.[stage] ??
    order[`${stage}_proofs`]?.length ??
    proofs.length;

  if (proofs.length === 0) {
    return (
      <span className="flex items-center gap-1 text-gray-300 text-xs">
        <ImageIcon className="w-3 h-3" />
        —
      </span>
    );
  }

  const MAX_SHOWN = 3;
  const shown = proofs.slice(0, MAX_SHOWN);
  const extra = totalCount - shown.length;

  return (
    <>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {shown.map((proof, i) => (
          <button
            key={proof.id || i}
            onClick={() => setLightboxUrl(proof.url)}
            className="relative flex-shrink-0 w-9 h-9 rounded overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-400"
            title={proof.filename || `Proof ${i + 1}`}
          >
            <img
              src={proof.url}
              alt={proof.filename || `Proof ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
        {extra > 0 && (
          <span className="flex-shrink-0 w-9 h-9 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500">
            +{extra}
          </span>
        )}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setLightboxUrl(null)}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxUrl}
              alt="Proof preview"
              className="max-w-full max-h-[90vh] object-contain"
            />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg leading-none hover:bg-black/80"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}
// ── End ProofThumbnails ───────────────────────────────────────────────────────

// Default columns — proofs column added between status and tracking_number
const DEFAULT_COLUMNS = [
  { id: 'checkbox',        label: '',               width: '40px',  visible: true,  sortable: false },
  { id: 'order_id',        label: 'Order ID',        width: '120px', visible: true,  sortable: true,  sortKey: 'order_number' },
  { id: 'order_date',      label: 'Order Date',      width: '180px', visible: true,  sortable: true,  sortKey: 'created_at' },
  { id: 'email',           label: 'Email',           width: '200px', visible: true,  sortable: true,  sortKey: 'customer_email' },
  { id: 'folder',          label: 'Folder',          width: '150px', visible: true,  sortable: true,  sortKey: 'stage' },
  { id: 'name',            label: 'Name',            width: '150px', visible: true,  sortable: true,  sortKey: 'customer_name' },
  { id: 'stage',           label: 'Stage',           width: '120px', visible: true,  sortable: true,  sortKey: 'stage' },
  { id: 'status',          label: 'Status',          width: '150px', visible: true,  sortable: true,  sortKey: 'status' },
  { id: 'proofs',          label: 'Latest Proofs',   width: '140px', visible: true,  sortable: false },
  { id: 'tracking_number', label: 'Tracking Number', width: '150px', visible: true,  sortable: true,  sortKey: 'tracking_number' },
  { id: 'carrier',         label: 'Carrier',         width: '120px', visible: true,  sortable: true,  sortKey: 'carrier' },
  { id: 'last_updated',    label: 'Last Updated',    width: '180px', visible: true,  sortable: true,  sortKey: 'updated_at' },
];

// Neon status/stage palette now lives in @/lib/statusColors (shared across all admin pages).

function FolderItem({ folder, onClick, count, selectedFolder }) {
  const isThisFolderActive = selectedFolder === folder.id;
  // Parse a status id out of "stage:status" folder ids so status folders glow in their own color
  const folderStatusId = typeof folder.id === 'string' && folder.id.includes(':') ? folder.id.split(':')[1] : null;
  const c = folderStatusId ? getStatusColors(folderStatusId) : null;
  return (
    <div
      onClick={() => onClick(folder.id)}
      className={`
        flex items-center justify-between px-3 py-1.5 cursor-pointer transition-colors text-xs rounded-md
        ${isThisFolderActive ? 'font-semibold' : 'text-gray-700 hover:bg-gray-100'}
        ${folder.isCategory ? 'font-bold uppercase mt-2' : 'ml-4'}
      `}
      style={isThisFolderActive ? { background: 'linear-gradient(90deg,#eef2ff,#f5ecff)', color: '#4f2fd0' } : undefined}
    >
      <span className="truncate flex items-center gap-2">
        {c && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.accent }} />}
        {folder.label}
      </span>
      {count > 0 && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0 font-semibold"
          style={
            folder.isCategory
              ? { background: 'linear-gradient(135deg,#7b3ff2,#ff4d8f)', color: '#ffffff' }
              : c
                ? { backgroundColor: c.tint, color: c.accent }
                : { backgroundColor: '#e5e7eb', color: '#374151' }
          }
        >
          {count}
        </span>
      )}
    </div>
  );
}

function DraggableColumnItem({ column, toggleColumnVisibility }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded border">
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <Checkbox checked={column.visible} onCheckedChange={() => toggleColumnVisibility(column.id)} />
      <span className="text-sm">{column.label}</span>
    </div>
  );
}

export default function OrderDesk() {
  const navigate = useNavigate();
  const { workflowConfig, loading: brandingLoading } = useBranding();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkStage, setBulkStage] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const retryCountRef = useRef(0);

  const workflowStages = workflowConfig?.stages || [];
  const timerRules = workflowConfig?.timers || [];

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [orderCounts, setOrderCounts] = useState({ total: 0, archived: 0, by_stage: {}, status_counts: {} });
  const ORDERS_PER_PAGE = 40;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const savedColumns = localStorage.getItem('orderdesk_columns');
    if (savedColumns) {
      try {
        const saved = JSON.parse(savedColumns);
        // Merge saved with DEFAULT_COLUMNS to pick up any new columns (like 'proofs')
        // that weren't in the user's saved config yet.
        const merged = DEFAULT_COLUMNS.map(def => {
          const match = saved.find(s => s.id === def.id);
          return match ? { ...def, ...match } : def;
        });
        setColumns(merged);
      } catch {
        setColumns(DEFAULT_COLUMNS);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAuthReady(true);
    fetchOrderCounts();
    const shouldRefresh = sessionStorage.getItem('orders_cache_invalidate');
    if (shouldRefresh) {
      sessionStorage.removeItem('orders_cache_invalidate');
      Object.keys(sessionStorage).forEach(key => { if (key.startsWith('orders_cache')) sessionStorage.removeItem(key); });
    }
  }, [navigate]);

  useEffect(() => { if (authReady) fetchOrders(); }, [currentPage, selectedFolder, searchDebounce, authReady]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchDebounce(searchQuery); setCurrentPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchOrderCounts = async () => {
    try {
      const response = await fetchWithRetry(() => axios.get(`${API}/admin/orders/counts`, { timeout: 15000 }), 3, 1000);
      setOrderCounts(response.data);
    } catch (error) { console.error("Failed to load order counts:", error); }
  };

  const getOrderBackgroundColor = (order) => {
    if (!order.updated_at) return '';
    const stage = order.stage;
    const status = order[`${stage}_status`];
    const hoursDiff = (new Date() - new Date(order.updated_at)) / (1000 * 60 * 60);
    const matchingRule = timerRules.find(rule =>
      rule.stage?.toLowerCase() === stage?.toLowerCase() &&
      rule.status?.toLowerCase().replace(/\s+/g, '_') === status?.toLowerCase()
    );
    if (matchingRule) {
      const thresholdHours = (matchingRule.days || 0) * 24 + (matchingRule.hours || 0);
      if (hoursDiff >= thresholdHours) return matchingRule.backgroundColor || '';
    }
    return '';
  };

  const fetchOrders = async (isRetry = false) => {
    if (!isRetry) { setLoading(true); setLoadError(null); retryCountRef.current = 0; }
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', ORDERS_PER_PAGE);
      if (selectedFolder === 'archived') {
        params.append('archived', 'true');
      } else if (selectedFolder !== 'all') {
        params.append('archived', 'false');
        const [stage, status] = selectedFolder.split(':');
        if (stage) params.append('stage', stage);
        if (status) params.append('status', status);
      } else {
        params.append('archived', 'false');
      }
      if (searchDebounce) params.append('search', searchDebounce);
      // Tell the backend to include latest proof thumbnails
      params.append('include_latest_proofs', 'true');

      const cacheKey = `orders_cache_${params.toString()}`;
      const cacheTimestampKey = `orders_cache_timestamp_${params.toString()}`;
      const CACHE_DURATION = 30 * 1000;

      if (!searchDebounce && !isRetry) {
        const cachedData = sessionStorage.getItem(cacheKey);
        const cachedTimestamp = sessionStorage.getItem(cacheTimestampKey);
        if (cachedData && cachedTimestamp && (Date.now() - parseInt(cachedTimestamp)) < CACHE_DURATION) {
          const data = JSON.parse(cachedData);
          setOrders(data.orders || data);
          if (data.pagination) { setTotalPages(data.pagination.total_pages); setTotalCount(data.pagination.total_count); }
          setLoading(false);
          fetchOrdersBackground(params.toString(), cacheKey, cacheTimestampKey);
          return;
        }
      }

      const response = await fetchWithRetry(
        () => axios.get(`${API}/admin/orders?${params.toString()}`, { timeout: 30000 }), 3, 1000
      );
      const data = response.data;

      if (!searchDebounce) {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
      }

      if (data.orders) {
        setOrders(data.orders);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } else {
        setOrders(data);
        setTotalCount(data.length);
        setTotalPages(Math.ceil(data.length / ORDERS_PER_PAGE));
      }
      setLoadError(null);
      retryCountRef.current = 0;
    } catch (error) {
      console.error("Failed to load orders:", error);
      setLoadError(error.message || "Failed to load orders. Please try again.");
      if (retryCountRef.current === 0) toast.error("Having trouble loading orders. Retrying...");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersBackground = async (queryString, cacheKey, cacheTimestampKey) => {
    try {
      const response = await axios.get(`${API}/admin/orders?${queryString}`);
      const data = response.data;
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
      sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
      if (data.orders) { setOrders(data.orders); setTotalPages(data.pagination.total_pages); setTotalCount(data.pagination.total_count); }
    } catch (error) { console.error("Background fetch failed:", error); }
  };

  const sortedOrders = [...orders].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
    if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') { aVal = new Date(aVal); bVal = new Date(bVal); }
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (columnId) => {
    const column = columns.find(c => c.id === columnId);
    const sortKey = column?.sortKey || columnId;
    setSortConfig(prev => ({ key: sortKey, direction: prev.key === sortKey && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleColumnDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newColumns = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('orderdesk_columns', JSON.stringify(newColumns));
        return newColumns;
      });
    }
  };

  const toggleColumnVisibility = (columnId) => {
    const newColumns = columns.map(col => col.id === columnId ? { ...col, visible: !col.visible } : col);
    setColumns(newColumns);
    localStorage.setItem('orderdesk_columns', JSON.stringify(newColumns));
  };

  const resetColumns = () => {
    setColumns(DEFAULT_COLUMNS);
    localStorage.setItem('orderdesk_columns', JSON.stringify(DEFAULT_COLUMNS));
    toast.success("Columns reset to default");
  };

  const handleSelectAll = (checked) => {
    if (checked) { setSelectedOrders(sortedOrders.map(o => o.id)); }
    else { setSelectedOrders([]); setSelectAllMatching(false); }
  };

  const handleSelectOrder = (orderId, checked) => {
    if (checked) { setSelectedOrders([...selectedOrders, orderId]); }
    else { setSelectedOrders(selectedOrders.filter(id => id !== orderId)); setSelectAllMatching(false); }
  };

  const isOrderSelected = (orderId) => selectedOrders.includes(orderId);
  const allSelected = sortedOrders.length > 0 && selectedOrders.length === sortedOrders.length;
  const effectiveBulkCount = selectAllMatching ? totalCount : selectedOrders.length;

  const getCurrentFilter = useCallback(() => {
    const filter = { search: searchDebounce || null };
    if (selectedFolder === 'archived') { filter.archived = true; }
    else if (selectedFolder !== 'all') {
      filter.archived = false;
      const [stage, status] = selectedFolder.split(':');
      if (stage) filter.stage = stage;
      if (status) filter.status = status;
    } else { filter.archived = false; }
    return filter;
  }, [selectedFolder, searchDebounce]);

  const handleMouseDown = (e, columnId) => {
    const column = columns.find(c => c.id === columnId);
    setResizingColumn(columnId); setStartX(e.pageX); setStartWidth(parseInt(column.width));
  };
  const handleMouseMove = (e) => {
    if (!resizingColumn) return;
    const newWidth = Math.max(50, startWidth + (e.pageX - startX));
    const newColumns = columns.map(col => col.id === resizingColumn ? { ...col, width: `${newWidth}px` } : col);
    setColumns(newColumns);
    localStorage.setItem('orderdesk_columns', JSON.stringify(newColumns));
  };
  const handleMouseUp = () => { if (resizingColumn) setResizingColumn(null); };

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
    }
  }, [resizingColumn, startX, startWidth]);

  const handleExport = async () => {
    toast.info("Fetching all orders for export...");
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders?limit=10000`, { headers: { Authorization: `Bearer ${token}` } });
      const allOrders = response.data.orders || response.data || [];
      if (allOrders.length === 0) { toast.error("No orders to export"); return; }
      const headers = ['Order ID','Order Number','Customer Name','Customer Email','Stage','Status','Order Date','Last Updated','Clay Status','Paint Status','Shipped Status','Product Details','Special Instructions','Tracking Number'];
      const csvContent = [
        headers.join(','),
        ...allOrders.map(order => {
          const status = order[`${order.stage}_status`] || '';
          return [order.id,order.order_number,order.customer_name,order.customer_email,order.stage,status,order.created_at,order.updated_at,order.clay_status,order.paint_status,order.shipped_status,order.product_details,order.special_instructions,order.tracking_number]
            .map(val => `"${String(val||'').replace(/"/g,'""')}"`).join(',');
        })
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-full-export-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success(`Exported ${allOrders.length} orders successfully`);
    } catch (error) { console.error("Export error:", error); toast.error("Failed to export orders"); }
  };

  const handleSendReminderEmails = async () => {
    if (selectedOrders.length === 0) { toast.error("Please select at least one order"); return; }
    setSendingReminders(true);
    try {
      await axios.post(`${API}/orders/bulk-reminder-emails`, { order_ids: selectedOrders });
      toast.success(`Reminder emails sent to ${selectedOrders.length} customer(s)`);
      setSelectedOrders([]);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send reminder emails"); }
    finally { setSendingReminders(false); }
  };

  const handleSyncOrders = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/sync-orders`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message || `Synced ${response.data.synced || 0} new orders from Shopify`);
      await Promise.all([fetchOrders(), fetchOrderCounts()]);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to sync orders from Shopify"); }
    finally { setSyncing(false); }
  };

  const handleArchiveOrders = async () => {
    if (selectedOrders.length === 0) { toast.error("Please select at least one order"); return; }
    const isArchiving = selectedFolder !== 'archived';
    try {
      await axios.post(`${API}/orders/bulk-archive`, { order_ids: selectedOrders, archived: isArchiving });
      toast.success(`${selectedOrders.length} order(s) ${isArchiving ? 'archived' : 'unarchived'} successfully`);
      setSelectedOrders([]);
      await Promise.all([fetchOrders(), fetchOrderCounts()]);
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${isArchiving ? 'archive' : 'unarchive'} orders`); }
  };

  const handleBulkUpdateStageStatus = async () => {
    if (!bulkStage || !bulkStatus) { toast.error("Please select both stage and status"); return; }
    if (selectedOrders.length === 0) { toast.error("No orders selected"); return; }
    setIsBulkUpdating(true);
    try {
      const response = await axios.post(`${API}/admin/orders/bulk-update`, { order_ids: selectedOrders, stage: bulkStage, status: bulkStatus });
      toast.success(`Updated ${response.data.success_count} order(s) to ${bulkStage}/${bulkStatus}`);
      setBulkUpdateDialogOpen(false); setBulkStage(''); setBulkStatus(''); setSelectedOrders([]);
      await Promise.all([fetchOrders(), fetchOrderCounts()]);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update orders"); }
    finally { setIsBulkUpdating(false); }
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      let response;
      if (selectAllMatching) {
        const filter = getCurrentFilter();
        response = await axios.post(`${API}/admin/orders/bulk-delete-by-filter`, { ...filter, confirm: true, expected_count: totalCount });
      } else {
        if (selectedOrders.length === 0) { toast.error("No orders selected"); setIsDeleting(false); return; }
        response = await axios.post(`${API}/admin/orders/bulk-delete`, { order_ids: selectedOrders, confirm: true });
      }
      Object.keys(sessionStorage).forEach(key => { if (key.startsWith('orders_cache')) sessionStorage.removeItem(key); });
      toast.success(`Deleted ${response.data.deleted_count} order(s)`);
      setDeleteDialogOpen(false); setSelectedOrders([]); setSelectAllMatching(false); setCurrentPage(1);
      await Promise.all([fetchOrders(), fetchOrderCounts()]);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete orders"); }
    finally { setIsDeleting(false); }
  };

  const getBulkStatusOptions = () => {
    if (!bulkStage) return [];
    return workflowStages.find(s => s.id === bulkStage)?.statuses || [];
  };

  const folderStructure = [
    { id: 'all', label: 'All Orders', count: orderCounts.total - orderCounts.archived, isCategory: false },
    ...workflowStages.filter(stage => stage.id !== 'archived').map(stage => ({
      id: stage.id, label: stage.name.toUpperCase(), isCategory: true,
      count: orderCounts.by_stage?.[stage.id] || 0,
      children: (stage.statuses || []).map(status => ({
        id: `${stage.id}:${status.id}`,
        label: `${stage.name} - ${status.name}`,
        count: orderCounts.status_counts?.[stage.id]?.[status.id] || 0
      }))
    })),
    { id: 'archived', label: 'ARCHIVED', isCategory: true, count: orderCounts.archived || 0 }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const visibleColumns = columns.filter(c => c.visible);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="w-full justify-start text-blue-600 hover:text-blue-700 mb-4">
            <FolderOpen className="w-4 h-4 mr-2" />Dashboard
          </Button>
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 px-4">FOLDERS</h3>
            <div>
              {folderStructure.map((folder) => (
                <div key={folder.id}>
                  <FolderItem folder={folder} onClick={(folderId) => { setSelectedFolder(folderId); setCurrentPage(1); setSelectedOrders([]); }} count={folder.count} selectedFolder={selectedFolder} />
                  {folder.children && folder.children.map((child) => (
                    <FolderItem key={child.id} folder={child} onClick={(folderId) => { setSelectedFolder(folderId); setCurrentPage(1); setSelectedOrders([]); }} count={child.count} selectedFolder={selectedFolder} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-extrabold bg-gradient-to-r from-fuchsia-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">All Orders</h1>
            <div className="flex items-center gap-2">
              <Button onClick={handleSyncOrders} disabled={syncing} variant="outline" size="sm" className="border-green-200 text-green-600 hover:bg-green-50">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Orders'}
              </Button>
              <Button onClick={() => navigate('/admin/users')} variant="outline" size="sm" className="border-purple-200 text-purple-600 hover:bg-purple-50">
                <UserIcon className="w-4 h-4 mr-2" />Users
              </Button>
              <Button onClick={() => navigate('/admin/settings')} variant="outline" size="sm" className="border-blue-200 text-blue-600 hover:bg-blue-50">
                <Settings className="w-4 h-4 mr-2" />Settings
              </Button>
              <Button variant="ghost" size="icon" onClick={() => window.print()}><Printer className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon"><Info className="w-4 h-4" /></Button>
              <Button onClick={() => { localStorage.removeItem('admin_token'); navigate('/admin/login'); toast.success('Logged out successfully'); }} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">
                Logout
              </Button>
              <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm"><Settings2 className="w-4 h-4 mr-2" />Customize</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Customize Columns</DialogTitle>
                    <DialogDescription>Show/hide columns and drag to reorder them</DialogDescription>
                  </DialogHeader>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
                    <SortableContext items={columns.filter(c => c.id !== 'checkbox').map(c => c.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {columns.filter(c => c.id !== 'checkbox').map((column) => (
                          <DraggableColumnItem key={column.id} column={column} toggleColumnVisibility={toggleColumnVisibility} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <Button onClick={resetColumns} variant="outline" className="w-full">Reset to Default</Button>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search orders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-64" />
              </div>
              <span className="text-sm text-gray-600">{totalCount} Orders Found {totalPages > 1 && `(Page ${currentPage} of ${totalPages})`}</span>
              {selectedOrders.length > 0 && <span className="text-sm text-blue-600 font-semibold">{selectedOrders.length} Selected</span>}
            </div>
            <div className="flex items-center gap-2">
              {selectedOrders.length > 0 && (
                <>
                  <Button onClick={() => setBulkUpdateDialogOpen(true)} variant="outline" size="sm" className="border-blue-400 text-blue-700 hover:bg-blue-50" data-testid="bulk-change-stage-btn">
                    <Edit className="w-4 h-4 mr-2" />Change Stage ({effectiveBulkCount})
                  </Button>
                  <Button onClick={handleArchiveOrders} variant="outline" size="sm" className="border-gray-400 text-gray-700 hover:bg-gray-100" data-testid="bulk-archive-btn">
                    <Archive className="w-4 h-4 mr-2" />{selectedFolder === 'archived' ? `Unarchive (${selectedOrders.length})` : `Archive (${selectedOrders.length})`}
                  </Button>
                  <Button onClick={handleSendReminderEmails} disabled={sendingReminders} variant="default" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-reminder-btn">
                    <Bell className="w-4 h-4 mr-2" />{sendingReminders ? 'Sending...' : `Send Reminder (${selectedOrders.length})`}
                  </Button>
                  <Button onClick={() => setDeleteDialogOpen(true)} variant="outline" size="sm" className="border-red-400 text-red-700 hover:bg-red-50" data-testid="bulk-delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" />Delete ({effectiveBulkCount})
                  </Button>
                </>
              )}
              <Button onClick={handleExport} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />Export All Data
              </Button>
            </div>
          </div>
        </div>

        {/* Select all matching banner */}
        {allSelected && totalCount > sortedOrders.length && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 text-sm text-blue-800 flex items-center justify-center gap-3" data-testid="select-all-matching-banner">
            {!selectAllMatching ? (
              <>
                <span>All <strong>{sortedOrders.length}</strong> orders on this page are selected.</span>
                <button className="font-semibold underline hover:text-blue-900" onClick={() => setSelectAllMatching(true)} data-testid="select-all-matching-btn">
                  Select all {totalCount} orders matching this filter
                </button>
              </>
            ) : (
              <>
                <span>All <strong>{totalCount}</strong> orders matching this filter are selected.</span>
                <button className="font-semibold underline hover:text-blue-900" onClick={() => { setSelectAllMatching(false); setSelectedOrders([]); }} data-testid="clear-all-matching-btn">
                  Clear selection
                </button>
              </>
            )}
          </div>
        )}

        {/* Order Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full"><p className="text-gray-500">Loading orders...</p></div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  {visibleColumns.map((column) => {
                    if (column.id === 'checkbox') {
                      return (
                        <th key={column.id} style={{ width: column.width }} className="p-3 text-left bg-gray-50 border-b border-gray-200">
                          <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                        </th>
                      );
                    }
                    const isSorted = sortConfig?.key === (column.sortKey || column.id);
                    return (
                      <th key={column.id} style={{ width: column.width, position: 'relative' }} className="p-3 text-left bg-gray-50 border-b border-gray-200">
                        <button
                          onClick={() => column.sortable && handleSort(column.id)}
                          className={`flex items-center gap-1 font-semibold text-sm ${column.sortable ? 'text-gray-700 hover:text-gray-900 cursor-pointer' : 'text-gray-500 cursor-default'}`}
                        >
                          {column.label}
                          {column.sortable && <span className="text-gray-400">{isSorted ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}</span>}
                        </button>
                        <div onMouseDown={(e) => handleMouseDown(e, column.id)} className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 hover:w-1.5" style={{ userSelect: 'none' }} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((order, idx) => (
                  <tr
                    key={order.id}
                    onClick={(e) => {
                      if (e.target.type === 'checkbox' || e.target.closest('[role="checkbox"]')) return;
                      sessionStorage.setItem('orderDetailsReturnPath', '/admin/orderdesk');
                      navigate(`/admin/orders/${order.id}`);
                    }}
                    className={`border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    style={{ backgroundColor: getOrderBackgroundColor(order) || undefined }}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.id} className="p-3 text-sm">
                        {column.id === 'checkbox' && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={isOrderSelected(order.id)} onCheckedChange={(checked) => handleSelectOrder(order.id, checked)} />
                          </div>
                        )}
                        {column.id === 'order_id' && (
                          <span className="text-blue-700 font-semibold text-base inline-flex items-center gap-2" data-testid={`order-id-${order.order_number}`}>
                            {order.order_number}
                            {(() => {
                              let qty = order.total_quantity;
                              if (!qty && Array.isArray(order.line_items) && order.line_items.length > 0)
                                qty = order.line_items.reduce((sum, li) => sum + (parseInt(li.quantity, 10) || 1), 0);
                              if (qty && qty > 1) return (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200" title={`${qty} identical units in this order`}>
                                  <Package className="w-3 h-3" />Qty {qty}
                                </span>
                              );
                              return null;
                            })()}
                          </span>
                        )}
                        {column.id === 'order_date' && formatDate(order.created_at)}
                        {column.id === 'email' && order.customer_email}
                        {column.id === 'folder' && <span className="text-gray-600">{getStageLabel(order.stage, workflowConfig)}</span>}
                        {column.id === 'name' && order.customer_name}
                        {column.id === 'stage' && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold capitalize inline-block shadow-sm" style={{ backgroundColor: getStageColor(order.stage), color: '#ffffff' }}>
                            {getStageLabel(order.stage, workflowConfig)}
                          </span>
                        )}
                        {column.id === 'status' && (
                          <span
                            className="px-3 py-1 rounded-full text-xs font-bold capitalize inline-block shadow-sm"
                            style={statusBadgeStyle(order[`${order.stage}_status`], { glow: true })}
                          >
                            {getStatusLabel(order[`${order.stage}_status`], workflowConfig, order.stage)}
                          </span>
                        )}
                        {/* ── Proof thumbnails column ── */}
                        {column.id === 'proofs' && <ProofThumbnails order={order} />}
                        {column.id === 'tracking_number' && <TrackingLink trackingNumber={order.tracking_number} carrier={order.carrier || order.tracking_company} />}
                        {column.id === 'carrier' && <span className="text-gray-700">{order.carrier || order.tracking_company || '-'}</span>}
                        {column.id === 'last_updated' && formatDate(order.updated_at)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && !loadError && sortedOrders.length === 0 && (
            <div className="flex items-center justify-center h-64"><p className="text-gray-500">No orders found</p></div>
          )}

          {!loading && loadError && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <AlertCircle className="w-12 h-12 text-red-400" />
              <p className="text-gray-600 text-center max-w-md">{loadError}</p>
              <Button onClick={() => fetchOrders(true)} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Retry Loading Orders</Button>
            </div>
          )}

          {!loading && !loadError && totalPages > 1 && (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * ORDERS_PER_PAGE) + 1} - {Math.min(currentPage * ORDERS_PER_PAGE, totalCount)} of {totalCount} orders
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</Button>
                <span className="px-3 py-1 text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialogOpen} onOpenChange={setBulkUpdateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change Stage & Status</DialogTitle>
            <DialogDescription>Update {selectedOrders.length} selected order(s) to a new stage and status.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Stage</label>
              <Select value={bulkStage} onValueChange={(v) => { setBulkStage(v); setBulkStatus(''); }}>
                <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>{workflowStages.filter(s => s.id !== 'archived').map(stage => <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={bulkStatus} onValueChange={setBulkStatus} disabled={!bulkStage}>
                <SelectTrigger><SelectValue placeholder={bulkStage ? "Select status" : "Select stage first"} /></SelectTrigger>
                <SelectContent>{getBulkStatusOptions().map(status => <SelectItem key={status.id} value={status.id}>{status.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkUpdateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkUpdateStageStatus} disabled={!bulkStage || !bulkStatus || isBulkUpdating}>
              {isBulkUpdating ? 'Updating...' : `Update ${selectedOrders.length} Order(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />Permanently delete {effectiveBulkCount} order{effectiveBulkCount === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>This will permanently remove the selected orders from the database.</div>
                <div>Any sub-orders of the selected orders will also be deleted (cascade).</div>
                <div className="font-semibold text-red-700">This action cannot be undone.</div>
                {selectAllMatching && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                    You are about to delete <strong>every order matching the current filter</strong> — including orders on other pages you haven't viewed.
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="bulk-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleBulkDelete(); }} disabled={isDeleting} className="bg-red-600 hover:bg-red-700 focus:ring-red-600" data-testid="bulk-delete-confirm-btn">
              {isDeleting ? 'Deleting...' : `Delete ${effectiveBulkCount} order${effectiveBulkCount === 1 ? '' : 's'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}